import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { GameLayout, type IPlayer } from '@skillarea/game-layout';
import type { IGameState, TPlayer } from '@skillarea/game-tictactoe-phaser';

import type { TGamePlayer } from '../types';
import { usePhaserGameLoader } from '../use-phaser-game-loader';

import styles from './TicTacToeGame.module.scss';

import { playGameStartSound } from '~/lib/game-sounds';
import { GameLoadingBlock } from '~/components/Matchmaking/screens/GameScreen';
import { useGameLayoutLabels } from '~/hooks/useGameLayoutLabels';
import { useLocalizedGameName } from '~/hooks/useLocalizedGame';

export interface ITicTacToeGameResult {
  winner: TPlayer | null;
  isDraw: boolean;
}

export interface ITicTacToeGameProps {
  player1: TGamePlayer;
  player2: TGamePlayer;
  board: (TPlayer | null)[][];
  currentPlayer: TPlayer;
  player1TimeLeftMs: number;
  player2TimeLeftMs: number;
  turnDurationMs: number;
  isGameOver: boolean;
  winningLine?: { row: number; col: number }[];
  winner?: TPlayer | null;
  isDraw?: boolean;
  playerSymbol?: TPlayer;
  isMyTurn?: boolean;
  onCellClick?: (row: number, col: number) => void;
  onGameEnd?: (result: ITicTacToeGameResult) => void;
  onSurrender?: () => void;
  rulesContent?: string;
}

function player01ToSymbol(player: TPlayer): 'X' | 'O' {
  return player === 0 ? 'X' : 'O';
}

export const TicTacToeGame: FC<ITicTacToeGameProps> = ({
  board,
  currentPlayer,
  isDraw = false,
  isGameOver,
  isMyTurn = false,
  onCellClick,
  onGameEnd,
  onSurrender,
  player1: player1Info,
  player1TimeLeftMs,
  player2: player2Info,
  player2TimeLeftMs,
  playerSymbol = 0,
  rulesContent = '',
  turnDurationMs,
  winner,
  winningLine,
}) => {
  const gameLayoutLabels = useGameLayoutLabels();
  const gameName = useLocalizedGameName('tic-tac-toe', 'Tic-Tac-Toe');
  const containerRef = useRef<globalThis.HTMLDivElement>(null);

  type TTicTacToePhaserGame = {
    updateState: (state: IGameState) => void;
    setInteractive: (isInteractive: boolean) => void;
    destroy: () => void;
  };
  type TTicTacToePhaserGameConstructor = new (args: {
    parent: globalThis.HTMLDivElement;
    localPlayer: TPlayer;
    onCellClick: (row: number, col: number) => void;
    onGameEnd: (winner: TPlayer | null, isDraw: boolean) => void;
  }) => TTicTacToePhaserGame;

  const gameRef = useRef<TTicTacToePhaserGame | null>(null);
  const onCellClickRef = useRef(onCellClick);
  const onGameEndRef = useRef(onGameEnd);
  const playerSymbolRef = useRef(playerSymbol);
  onCellClickRef.current = onCellClick;
  onGameEndRef.current = onGameEnd;
  playerSymbolRef.current = playerSymbol;

  const loadGame = useCallback(
    async (
      parent: globalThis.HTMLDivElement,
    ): Promise<TTicTacToePhaserGame> => {
      const mod = await import('@skillarea/game-tictactoe-phaser');
      const Game =
        mod.TicTacToePhaserGame as unknown as TTicTacToePhaserGameConstructor;
      const game = new Game({
        parent,
        localPlayer: playerSymbolRef.current,
        onCellClick: (row, col) => {
          onCellClickRef.current?.(row, col);
        },
        onGameEnd: (w, d) => {
          onGameEndRef.current?.({ winner: w, isDraw: d });
        },
      });
      const withReady = game as { whenReady?: () => Promise<void> };
      if (withReady.whenReady) await withReady.whenReady();
      return game;
    },
    [],
  );

  const player1: IPlayer = {
    id: 'player1',
    name: player1Info.name,
    avatarUrl: player1Info.avatarUrl,
    symbol: player01ToSymbol(playerSymbol),
  };

  const player2: IPlayer = {
    id: 'player2',
    name: player2Info.name,
    avatarUrl: player2Info.avatarUrl,
    symbol: playerSymbol === 0 ? 'O' : 'X',
  };

  const activePlayerId = isGameOver
    ? null
    : currentPlayer === playerSymbol
      ? 'player1'
      : 'player2';

  const { isReady } = usePhaserGameLoader({
    containerRef,
    gameRef,
    loadGame,
    onLoaded: playGameStartSound,
  });

  useEffect(() => {
    if (gameRef.current) {
      const gameState: IGameState = {
        board,
        isGameOver,
        winningLine,
        winner,
        isDraw,
      };
      gameRef.current.updateState(gameState);
      gameRef.current.setInteractive(isMyTurn && !isGameOver);
    }
  }, [board, isGameOver, winningLine, winner, isDraw, isMyTurn]);

  const gameContent = (
    <div className={styles.gameWrapper}>
      {!isReady ? <GameLoadingBlock className={styles.gameLoading} /> : null}
      <div className={styles.gameContainer} ref={containerRef} />
    </div>
  );

  return (
    <GameLayout
      activePlayerId={activePlayerId}
      gameContent={gameContent}
      gameName={gameName}
      labels={gameLayoutLabels}
      onSurrender={onSurrender}
      player1={player1}
      player1TimeLeftMs={player1TimeLeftMs}
      player2={player2}
      player2TimeLeftMs={player2TimeLeftMs}
      rulesContent={rulesContent}
      turnDurationMs={turnDurationMs}
    />
  );
};
